import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, map, switchMap, take } from 'rxjs/operators';

import { Select, Store } from '@ngxs/store';
import { FolderResourceState } from './state/state';

import { FolderResource, FileResource, FolderQueryOptions } from './folders.types';
import { CreateFileParams } from './folders.interface';
import { buildCreateFileFormData } from './folders.utils';
import { RelatedToType } from '@shared/enums';
import { AccountState } from '@state/account/state';

declare type ModuleBaseUrlType =
  | 'projects'
  | 'properties'
  | 'companies'
  | 'contacts'
  | 'tasks'
  | RelatedToType.CustomObjectValue
  | RelatedToType.TaskListTemplate;
interface FolderUrlOptions {
  extraPathUrl?: string;
  folderId?: number | string;
  fileId?: number | string;
  customObjectId?: number;
  task_list_template_id?: any;
  fileRelatedToType?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FoldersTabService {
  private _folder: BehaviorSubject<FolderResource | null>;
  private _file: BehaviorSubject<FileResource | null>;
  private _moduleId: string;
  private _moduleBaseUrl: ModuleBaseUrlType;
  private readonly _moduleBaseUrlTypes = ['projects', 'properties', 'contacts', 'companies', 'tasks'];
  private _accountId: string;
  private store = inject(Store);

  @Select(FolderResourceState.getFolder) folderSelector$: Observable<FolderResource>;

  constructor(private _httpClient: HttpClient) {
    this._folder = new BehaviorSubject(null);
    this._file = new BehaviorSubject(null);
  }

  get folder$(): Observable<FolderResource> {
    return this._folder.asObservable();
  }

  get file$(): Observable<FileResource> {
    return this._file.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set moduleId(val: string) {
    this._moduleId = val;
  }

  get moduleId(): string {
    return this._moduleId;
  }

  set moduleBaseUrl(val: ModuleBaseUrlType) {
    this._moduleBaseUrl = val;
  }

  get moduleBaseUrl() {
    return this._moduleBaseUrl;
  }

  searchFolders(search: string = ''): Observable<any> {
    const url = this.generateFolderResourceUrl({
      extraPathUrl: '/search',
    });

    return this._httpClient
      .post(url, {
        search: search,
      })
      .pipe(
        debounceTime(500),
        map((response) => {
          return response;
        }),
      );
  }

  getFolder(folderId?: number, customObjectId?: number, query?: FolderQueryOptions): Observable<FolderResource> {
    const url = this.generateFolderResourceUrl({
      folderId: folderId || '',
      fileId: undefined,
      extraPathUrl: '',
      customObjectId: customObjectId,
    });

    return this._httpClient.get<{ folder_resource: FolderResource }>(url, { params: this.toQueryParams(query) }).pipe(
      map(({ folder_resource }) => {
        this._folder.next(folder_resource);
        return folder_resource;
      }),
    );
  }

  getFile(folderId: number, fileResourceId: number): Observable<FileResource> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId: fileResourceId,
      extraPathUrl: '',
    });

    return this._httpClient.get<any>(url).pipe(
      map(({ file_resource }) => {
        this._file.next(file_resource);
        return file_resource;
      }),
    );
  }

  getFileById(fileId: number): Observable<FileResource> {
    const accountId = this.store.selectSnapshot(AccountState.getAccountId);

    return this._httpClient
      .get<{
        file_resource: FileResource;
      }>(`accounts/${accountId}/file_resources/${fileId}`)
      .pipe(map(({ file_resource }) => file_resource));
  }

  createFolder(folderResource: FolderResource, skipNext: boolean = false): Observable<FolderResource> {
    const url = this.generateFolderResourceUrl({});

    return this.folderSelector$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .post<any>(url, {
            folder_resource: folderResource,
          })
          .pipe(
            map((reponse) => {
              // Return the new contact
              return reponse.folder_resource;
            }),
          ),
      ),
    );
  }

  updateFolder(folderResource: FolderResource): Observable<FolderResource> {
    const url = this.generateFolderResourceUrl({
      folderId: folderResource.id,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient
          .put<any>(url, {
            folder_resource: folderResource,
          })
          .pipe(
            map((reponse) => {
              // Return the new contact
              return reponse.folder_resource;
            }),
          ),
      ),
    );
  }

  createFile({ folderId, fileResource, file, options }: CreateFileParams): Observable<FileResource> {
    const urlOptions: FolderUrlOptions = {
      folderId: folderId,
      fileRelatedToType: fileResource?.related_to_type,
    };

    if (options?.customObjectId) {
      urlOptions['customObjectId'] = options.customObjectId;
    }

    if (options?.task_list_template_id != null) {
      urlOptions['task_list_template_id'] = options.task_list_template_id;
    }

    const url = this.generateFileResourceUrl(urlOptions);
    const body = file ? buildCreateFileFormData(fileResource, file) : { file_resource: fileResource };

    return this._httpClient.post<any>(url, body).pipe(
      map((response) => {
        let newFile = response.file_resource;
        return newFile;
      }),
    );
  }

  deleteFile(folderId: number, fileResourceId: number, opts?: any): Observable<any> {
    const options = {
      folderId: folderId,
      fileId: fileResourceId,
    };

    if (opts?.task_list_template_id != null) {
      options['task_list_template_id'] = opts.task_list_template_id;
    }

    const url = this.generateFileResourceUrl(options);

    return this._httpClient.delete<any>(url);
  }

  deleteFolder(folderId: number): Observable<any> {
    const url = this.generateFolderResourceUrl({
      folderId: folderId,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient.delete<any>(url).pipe(
          map((response) => {
            response.folder_resource;
          }),
        ),
      ),
    );
  }

  updateFile(folderId: number, fileResource: FileResource): Observable<FileResource> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId: fileResource.id,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap((folder) =>
        this._httpClient.put<any>(url, { file_resource: fileResource }).pipe(
          map((response) => {
            let newFile = response.file_resource;

            return newFile;
          }),
        ),
      ),
    );
  }

  uploadDocument(folderId: number, fileId: number, document: any, opts?: any): Observable<FileResource> {
    const uploadData = new FormData();
    uploadData.append('document[attachment]', document);

    const options: FolderUrlOptions = {
      folderId: folderId,
      fileId,
      extraPathUrl: `/upload_document`,
      fileRelatedToType: opts?.relatedToType,
    };

    if (opts?.customObjectId) {
      options['customObjectId'] = opts.customObjectId;
    }

    if (opts?.task_list_template_id != null) {
      options['task_list_template_id'] = opts.task_list_template_id;
    }

    const url = this.generateFileResourceUrl(options);

    return this.folderSelector$.pipe(
      take(1),
      switchMap((folder) =>
        this._httpClient.post<any>(url, uploadData).pipe(
          map((response) => {
            let updatedFileResource = response.file_resource;
            return updatedFileResource;
          }),
        ),
      ),
    );
  }

  updateRanks(list: any[], fileResource: string): Observable<FileResource> {
    let params = [];
    list.map((item, index) => params.push([fileResource, item.id, index]));
    return this.folderSelector$.pipe(
      take(1),
      switchMap((folder) =>
        this._httpClient.put<any>(`ranks`, { ranks: params }).pipe(
          map((response) => {
            let newFile;
            if (response) {
              newFile = response.file_resource;
            }

            return newFile;
          }),
        ),
      ),
    );
  }

  deleteDocument(folderId: number, fileId: number, documentId: number): Observable<any> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId,
      extraPathUrl: `/destroy_document?document_id=${documentId}`,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap((folder) =>
        this._httpClient.delete<any>(url).pipe(
          map((response) => {
            return response;
          }),
        ),
      ),
    );
  }

  getBase64Document(folderId: number, fileId: number, documentId: number): Observable<any> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId,
      extraPathUrl: `/download_base64?document_id=${documentId}`,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient.post<any>(url, {}).pipe(
          map((response) => {
            return response;
          }),
        ),
      ),
    );
  }

  uploadedExtractedDataToDocument(
    folderId: number,
    fileId: number,
    documentId: number,
    extactedData: any,
  ): Observable<any> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId,
      extraPathUrl: `/save_extracted_data?document_id=${documentId}`,
    });

    return this.folderSelector$.pipe(
      take(1),
      switchMap(() =>
        this._httpClient.put<any>(url, { extracted_data: extactedData }).pipe(
          map((response) => {
            return response;
          }),
        ),
      ),
    );
  }

  shareFolder(folderId, emails: string[] = [], message: string = ''): Observable<any> {
    const url = this.generateFolderResourceUrl({
      folderId: folderId,
      extraPathUrl: '/share',
    });

    return this._httpClient
      .post<any>(url, {
        emails: emails.join(','),
        message: message,
      })
      .pipe(
        map((response) => {
          return response;
        }),
      );
  }

  sendToDocusign(folderId: number, fileId: number): Observable<any> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId,
      extraPathUrl: '/send_to_docusign',
    });

    return this._httpClient.post<any>(url, {}).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  shareFile(folderId, fileResourceId, emails: string[] = [], message: string = ''): Observable<any> {
    const url = this.generateFileResourceUrl({
      folderId: folderId,
      fileId: fileResourceId,
      extraPathUrl: '/share',
    });

    return this._httpClient.post<any>(url, { emails: emails.join(','), message: message }).pipe(
      map((response) => {
        return response;
      }),
    );
  }

  private generateFileResourceUrl(params: FolderUrlOptions): string {
    const accountIdParam = params.extraPathUrl?.includes('?') ? `${params.extraPathUrl}` : ``;

    if (this.moduleBaseUrl === RelatedToType.CustomObjectValue && params.customObjectId) {
      return `accounts/${this._accountId}/custom_objects/${params.customObjectId}/values/${this.moduleId}
      /folder_resources/${params.folderId}/file_resources/${params.fileId || ''}${params.extraPathUrl || ''}`;
    }

    if (this.moduleBaseUrl === RelatedToType.TaskListTemplate) {
      return `/task_list_templates/${params.task_list_template_id}/folder_resources/${params.folderId}/file_resources/${params.fileId || ''}${params.extraPathUrl || ''}`;
    }
    // /api/v2/

    if (this._moduleBaseUrlTypes.includes(this.moduleBaseUrl)) {
      const folderResourcePath = params.folderId ? `folder_resources/${params.folderId}/` : '';

      return `${this.moduleBaseUrl}/${this.moduleId}/${folderResourcePath}file_resources/${
        params.fileId || ''
      }${params.extraPathUrl || ''}${accountIdParam}`;
    } else {
      const isTaskTargeted = params.fileRelatedToType === RelatedToType.Task;
      const relatedToQuery =
        !isTaskTargeted && this.moduleBaseUrl && this.moduleId
          ? `?related_to_type=${this.moduleBaseUrl}&related_to_id=${this.moduleId}`
          : '';
      return `accounts/${this._accountId}/folder_resources/${params.folderId}/file_resources/${
        params.fileId || ''
      }${params.extraPathUrl || ''}${relatedToQuery}`;
    }
  }

  private toQueryParams(query?: FolderQueryOptions): HttpParams {
    let params = new HttpParams();

    if (query?.recursive) {
      params = params.set('recursive', true);
    }

    if (query?.foldersOnly) {
      params = params.set('folders_only', true);
    }

    return params;
  }

  private generateFolderResourceUrl(params: FolderUrlOptions): string {
    const accountIdParam = params.extraPathUrl?.includes('?') ? `${params.extraPathUrl}` : ``;

    if (this.moduleBaseUrl === RelatedToType.CustomObjectValue && params.customObjectId) {
      return `accounts/${this._accountId}/custom_objects/${params.customObjectId}/values/${this.moduleId}/folder_resources`;
    }

    if (this._moduleBaseUrlTypes.includes(this.moduleBaseUrl)) {
      return `${this.moduleBaseUrl}/${this.moduleId}/folder_resources/${params.folderId || ''}${params.extraPathUrl || ''}${accountIdParam}`;
    } else {
      return `accounts/${this._accountId}/folder_resources/${params.folderId || ''}${params.extraPathUrl || ''}${
        this.moduleBaseUrl && this.moduleId
          ? `?related_to_type=${this.moduleBaseUrl}&related_to_id=${this.moduleId}`
          : ''
      }`;
    }
  }
}
